<template>
  <div class="cvmf-faixas" :style="{ background: corFundoCss }">
    <div class="cvmf-track">
      <div class="cvmf-track-fill" :style="{ width: progressoPercent + '%', background: corCss }" />

      <div class="cvmf-atual" :style="{ left: progressoPercent + '%' }">
        <div class="cvmf-atual-badge" :style="{ background: corCss }">{{ percentualLabel }}</div>
        <div class="cvmf-atual-seta" :style="{ borderTopColor: corCss }" />
      </div>

      <div
        v-for="limiar in limiares"
        :key="limiar.nome"
        class="cvmf-tick"
        :style="{ left: posicao(limiar.valor) + '%' }"
      >
        <div
          class="cvmf-tick-dot"
          :class="{ 'cvmf-tick-dot--atual': limiar.nome === classificacao }"
          :style="corTickDot(limiar)"
        />
        <div class="cvmf-tick-label">
          <span class="cvmf-tick-nome">{{ limiar.nome }}</span>
          <span class="cvmf-tick-valor">{{ limiar.valor }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Linha do tempo proporcional das faixas de atingimento da Regra 1 (Campanha de Vendas), usada tanto pro
// acumulado da campanha quanto pro mês corrente na visão do vendedor (DashboardVendas.vue). NÃO ATINGIU
// fica de fora dos "pontos" (não é uma faixa a alcançar, é onde todo mundo começa) — só BRONZE/PRATA/OURO
// aparecem como marcos, na posição real da escala (não igualmente espaçados), com um marcador extra pra
// onde o vendedor está agora.
export default {
  name: 'CampanhaVendaMargemFaixas',
  props: {
    // % de atingimento atual (bruto, sem formatação) — usado só pra calcular a posição na escala
    percentual: { type: Number, required: true },
    // mesmo valor já formatado pelo componente pai (formatarPercentual, com "%"), evita duplicar a regra de
    // formatação aqui
    percentualLabel: { type: String, required: true },
    classificacao: { type: String, default: null },
    // cor RGB já resolvida pelo componente pai (classificacaoCorRGBAcumulado — inclui a suavização de
    // NÃO ATINGIU), pra não duplicar essa regra de negócio de cor aqui
    corRgb: { type: Array, required: true },
    // escala máxima FORÇADA pelo pai (opcional) — quando a tela usa duas instâncias desse componente lado a
    // lado (acumulado + mês corrente, cada uma com seu próprio % de atingimento), cada uma calculando sua
    // própria escala faria BRONZE/PRATA/OURO ficarem em posições horizontais diferentes entre as duas barras
    // (a escala interna depende do `percentual` de CADA instância). Passando a mesma escala pras duas, os
    // 3 marcos ficam alinhados verticalmente entre as barras. null usa o cálculo interno (uso isolado).
    escalaMax: { type: Number, default: null },
  },
  data() {
    return {
      limiares: [
        { nome: 'BRONZE', valor: 85 },
        { nome: 'PRATA', valor: 95 },
        { nome: 'OURO', valor: 102 },
      ],
    };
  },
  computed: {
    // escala dinâmica: sempre cabe o OURO (102%) com uma margem pequena, e cresce se o vendedor já estiver
    // bem acima disso, pra o marcador atual nunca ficar espremido na borda direita. A margem é enxuta de
    // propósito (8 pontos, não mais) — toda folga aqui "rouba" espaço proporcional bem mais precioso do
    // trecho 85%-102% (onde ficam os 3 marcos que realmente importam), que já é naturalmente estreito.
    // Usa a escala forçada via prop quando ela vem preenchida (ver comentário da prop escalaMax).
    escalaMaxCalculada() {
      if (this.escalaMax != null) return this.escalaMax;
      return Math.max(108, Math.ceil((this.percentual + 8) / 5) * 5);
    },
    corCss() {
      return `rgb(${this.corRgb.join(',')})`;
    },
    // fundo bem sutil (mesma cor da classificação atual, quase transparente) — dá um respiro visual pro
    // componente se destacar como um bloco próprio dentro da caixa, sem competir com o resto do card
    corFundoCss() {
      return `rgba(${this.corRgb.join(',')}, 0.06)`;
    },
    progressoPercent() {
      return this.posicao(this.percentual);
    },
  },
  methods: {
    // a faixa 0%-85% (NÃO ATINGIU) é só "zona morta" antes do primeiro prêmio — numa escala linear ela
    // sozinha ocupa a maior parte da largura, espremendo BRONZE/PRATA/OURO num canto. Aqui ela é comprimida
    // pra caber só numa fração fixa da largura (zonaInicialFracao), e o resto do espaço é dividido, esse
    // sim proporcionalmente, entre 85% e a escala máxima — mantém uma noção de proporção (quanto mais perto
    // de 85% dentro da própria zona, mais a bolinha anda) sem deixar as faixas que importam ilegíveis
    posicao(valor) {
      const zonaInicialFracao = 0.42;
      if (valor <= 85) {
        return Math.max(0, (valor / 85) * zonaInicialFracao * 100);
      }
      const restante = (valor - 85) / (this.escalaMaxCalculada - 85);
      return Math.min(100, (zonaInicialFracao + restante * (1 - zonaInicialFracao)) * 100);
    },
    // preenche o "ponto" de uma faixa já ultrapassada (ex: já é PRATA, então o ponto do BRONZE fica cheio)
    // com a cor de marca da própria faixa (mesma paleta do mixin, usada nos chips/medalhas espalhados pelo
    // resto do dashboard) — a faixa atual usa corCss (já vem resolvida pelo pai, com a suavização de NÃO
    // ATINGIU quando cabe); as que ainda faltam alcançar ficam ocas, sem preenchimento
    corTickDot(limiar) {
      if (limiar.nome === this.classificacao) {
        return { background: this.corCss, borderColor: this.corCss };
      }
      if (this.percentual >= limiar.valor) {
        const cor = `rgb(${this.classificacaoCorRGB(limiar.nome).join(',')})`;
        return { background: cor, borderColor: cor };
      }
      return {};
    },
  },
};
</script>

<style scoped>
.cvmf-faixas {
  /* padding-top precisa passar de 38px (o quanto o badge do valor atual sobe: cvmf-atual-badge tem
     top: -38px) com uma folga extra, senão o badge fica mais perto do texto acima ("% da meta ... atingida")
     do que da própria trilha logo abaixo dele. Padding lateral maior (16px) pra "BRONZE 85%"/"OURO 102%"
     não ficarem colados na borda do fundo tintado; padding-bottom com folga extra (não só o suficiente pra
     caber o texto "85%"/"95%"/"102%") pra sobrar respiro entre esse texto e a borda do fundo tintado;
     margem vertical própria pra não espremer contra a legenda/mensagem em volta */
  padding: 46px 16px 50px;
  margin: 6px 0;
  border-radius: 10px;
  transition: background 0.3s ease;
}
.cvmf-track {
  position: relative;
  height: 6px;
  background: #e2e8f0;
  border-radius: 4px;
}
.cvmf-track-fill {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  border-radius: 4px;
  transition: width 0.3s ease;
}
.cvmf-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
}
.cvmf-tick-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #cbd5e1;
  box-sizing: border-box;
  margin: -2px auto 0;
}
.cvmf-tick-dot--atual {
  width: 14px;
  height: 14px;
  margin-top: -4px;
  box-shadow: 0 0 0 4px rgba(2, 52, 73, 0.08);
}
.cvmf-tick-label {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
}
.cvmf-tick-nome {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.cvmf-tick-valor {
  font-size: 12px;
  font-weight: 600;
  color: #023449;
  white-space: nowrap;
}
.cvmf-atual {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  z-index: 2;
}
/* seta (não bolinha) de propósito: quando o valor atual cai perto de um marco (BRONZE/PRATA/OURO), duas
   bolinhas da mesma cor quase coincidindo viram uma mancha só, difícil de separar visualmente qual é qual.
   Uma seta apontando pra baixo, flutuando ACIMA da trilha (sem tocar os pontos dos marcos, que continuam
   embaixo, sobre a trilha), deixa claro que são duas coisas diferentes mesmo quando estão na mesma posição
   horizontal. O badge fica bem mais acima (ver top do cvmf-atual-badge) pra sobrar respiro entre os dois,
   em vez de espremer os dois juntos ou grudar uma haste entre eles (que ficava com cara de bandeirinha) */
.cvmf-atual-seta {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid;
  margin: -11px auto 0;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.35));
}
.cvmf-atual-badge {
  position: absolute;
  top: -38px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 5px;
  white-space: nowrap;
}

/* telas estreitas: BRONZE/PRATA/OURO ficam a poucos % de distância entre si (85/95/102), então em pouco
   espaço horizontal as legendas colam umas nas outras — encolhe fonte, ponto e badge pra abrir mais folga
   relativa entre elas, sem mudar a lógica de posicionamento */
@media (max-width: 420px) {
  .cvmf-faixas {
    padding: 40px 2px 44px;
  }
  .cvmf-tick-nome {
    font-size: 9px;
    letter-spacing: 0.2px;
  }
  .cvmf-tick-valor {
    font-size: 10px;
  }
  .cvmf-tick-dot {
    width: 8px;
    height: 8px;
    margin-top: -1px;
  }
  .cvmf-tick-dot--atual {
    width: 12px;
    height: 12px;
    margin-top: -3px;
  }
  .cvmf-atual-seta {
    border-left-width: 5px;
    border-right-width: 5px;
    border-top-width: 7px;
    margin-top: -10px;
  }
  .cvmf-atual-badge {
    top: -32px;
    font-size: 10px;
    padding: 2px 6px;
  }
}
</style>
